import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

const OUTER_COLOR = '#C4C4C4';
const INNER_COLOR = '#AAAAAA';
const INNER_SCALE = 0.75;
const SPIN_MS = 3000;
const CYCLE_MS = 2500;

const VIEWBOX = 100;
const CENTER = VIEWBOX / 2;
const R = 44;
const C = 2 * Math.PI * R;

const cycleCCW = keyframes`
  0% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${C};
  }
  50.01% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${-C};
  }
  100% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
`;

const cycleCW = keyframes`
  0% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${-C};
  }
  50.01% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: ${C};
  }
  100% {
    stroke-dasharray: ${C} ${C};
    stroke-dashoffset: 0;
  }
`;

const spinCCW = keyframes`
  to {
    transform: rotate(-360deg);
  }
`;

const spinCW = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Svg = styled.svg`
  display: inline-block;
  flex-shrink: 0;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
`;

const ScaleWrapper = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  transform: scale(${INNER_SCALE});
`;

const OuterGroup = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  will-change: transform;
  animation: ${spinCCW} ${SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: ${SPIN_MS * 2}ms;
  }
`;

const InnerGroup = styled.g`
  transform-box: view-box;
  transform-origin: ${CENTER}px ${CENTER}px;
  will-change: transform;
  animation: ${spinCW} ${SPIN_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: ${SPIN_MS * 2}ms;
  }
`;

const ArcBase = styled.circle`
  fill: none;
  stroke-linecap: round;
  will-change: stroke-dashoffset;
`;

const OuterArc = styled(ArcBase)`
  animation: ${cycleCCW} ${CYCLE_MS}ms linear infinite;
`;

const InnerArc = styled(ArcBase)`
  animation: ${cycleCW} ${CYCLE_MS}ms linear infinite;
`;

const _toCssSize = (size) =>
  typeof size === 'number' ? `${size}px` : size || '24px';

export const YouSpinner = ({ size, color, thickness = 6, ...rest }) => {
  const sw = Number.parseFloat(thickness) || 6;
  return (
    <Svg
      role="status"
      aria-label="Carregando"
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      $size={_toCssSize(size)}
      {...rest}
    >
      <OuterGroup>
        <OuterArc
          cx={CENTER}
          cy={CENTER}
          r={R}
          strokeWidth={sw}
          style={{ stroke: color || OUTER_COLOR }}
        />
      </OuterGroup>
      <ScaleWrapper>
        <InnerGroup>
          <InnerArc
            cx={CENTER}
            cy={CENTER}
            r={R}
            strokeWidth={sw}
            style={{ stroke: INNER_COLOR }}
          />
        </InnerGroup>
      </ScaleWrapper>
    </Svg>
  );
};

YouSpinner.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default YouSpinner;
